import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

export interface SenderDomainDnsRecord {
  type: string;
  name: string;
  value: string;
  purpose?: string;
  valid?: boolean;
}

export interface SenderDomain {
  id: string;
  userId: string;
  domain: string;
  subdomain: string;
  fromEmail: string;
  fromName: string | null;
  sendgridDomainId: string | null;
  verificationStatus: 'pending' | 'verified' | 'failed';
  dnsRecords: SenderDomainDnsRecord[];
  verifiedAt: string | null;
  lastValidationAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SenderDomainPayload {
  fromEmail: string;
  fromName?: string | null;
}

interface SenderDomainState {
  senderDomain: SenderDomain | null;
  featureConfigured: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isVerifying: boolean;
  isRemoving: boolean;
  error: string | null;
  fetchSenderDomain: () => Promise<void>;
  saveSenderDomain: (
    payload: SenderDomainPayload
  ) => Promise<{ success: boolean; message?: string }>;
  verifySenderDomain: () => Promise<{ success: boolean; verified: boolean; message?: string }>;
  removeSenderDomain: () => Promise<boolean>;
  clearError: () => void;
}

export const useSenderDomainStore = create<SenderDomainState>((set) => ({
  senderDomain: null,
  featureConfigured: false,
  isLoading: false,
  isSaving: false,
  isVerifying: false,
  isRemoving: false,
  error: null,

  fetchSenderDomain: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.get('/api/email/notifications/sender-domain');
      set({
        senderDomain: response.data?.senderDomain || null,
        featureConfigured: !!response.data?.featureConfigured,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load sender domain';
      logger.error('SenderDomainStore', 'Failed to fetch sender domain', { error });
      set({ error: errorMessage, isLoading: false });
    }
  },

  saveSenderDomain: async (payload) => {
    set({ isSaving: true, error: null });

    try {
      const response = await api.put('/api/email/notifications/sender-domain', payload);
      set({
        senderDomain: response.data?.senderDomain || null,
        isSaving: false,
      });
      return { success: true, message: response.data?.message };
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : 'Failed to save sender domain');
      logger.error('SenderDomainStore', 'Failed to save sender domain', { error: errorMessage });
      set({ error: errorMessage, isSaving: false });
      return { success: false, message: errorMessage };
    }
  },

  verifySenderDomain: async () => {
    set({ isVerifying: true, error: null });

    try {
      const response = await api.post('/api/email/notifications/sender-domain/verify');
      set({
        senderDomain: response.data?.senderDomain || null,
        isVerifying: false,
      });
      return {
        success: true,
        verified: !!response.data?.verified,
        message: response.data?.message,
      };
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            error?: string;
          };
        };
      };
      const statusCode = axiosError.response?.status;
      const fallbackVerifyMessage =
        typeof statusCode === 'number' && statusCode >= 500
          ? 'Unable to verify your domain right now. If you just added DNS records, wait a few minutes and try again.'
          : 'Failed to verify sender domain';
      const rawErrorMessage = error instanceof Error ? error.message.trim() : '';
      const isGenericStatusMessage = /^Request failed with status code \d{3}$/.test(rawErrorMessage);
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        (!isGenericStatusMessage ? rawErrorMessage : '') ||
        fallbackVerifyMessage;
      logger.error('SenderDomainStore', 'Failed to verify sender domain', { error: errorMessage });
      set({ error: errorMessage, isVerifying: false });
      return { success: false, verified: false, message: errorMessage };
    }
  },

  removeSenderDomain: async () => {
    set({ isRemoving: true, error: null });

    try {
      await api.delete('/api/email/notifications/sender-domain');
      set({
        senderDomain: null,
        isRemoving: false,
      });
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        axiosError.response?.data?.message ||
        (error instanceof Error ? error.message : 'Failed to remove sender domain');
      logger.error('SenderDomainStore', 'Failed to remove sender domain', { error: errorMessage });
      set({ error: errorMessage, isRemoving: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
